import React, {useState, useEffect, useMemo} from "react"
import {BasePopup} from "components/popup"
import {FormLabel} from "components/Form"
import {BaseEditor, BaseInput} from "components/inputs"
import {BaseButton} from "components/buttons"
import {BaseDatePicker} from "components/DateTimePicker"
import {KlassDropdown} from "components/Select"
import {TimezoneDropdown} from "components/Select/TimezoneDropdown"
import {Row, Col, Checkbox} from "antd"
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query"
import {calendarGroupsService, calendarService, eventTypeService} from "services"
import {useModel} from "hooks"
import moment, {Moment} from "moment"
import {useTranslation} from "react-i18next"
import {getFullName, getValuesChangeTwoObj, handleError, toastError, toastSuccess} from "helpers"
import {preventMinusCharacter} from "helpers/inputHelper"
import {Meeting, WeeklyHour, CreateMeetingPayload, EditMeetingPayload, MyCalendar} from "types/calendar"
import styles from "./MeetingModal.module.css"
import WeeklyHoursEditor from "./WeeklyHoursEditor"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {find, head, isEmpty, map} from "lodash"
import {useEdularModulesContext, Modules} from "@edular/modules"
import {REMINDERS} from "../common"
import {useAllLeadSources} from "hooks"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import {StudentStatus} from "types/students"

type Props = {
    isOpen: boolean
    meeting: Meeting | null
    onClose: () => void
    onSuccess: () => void
}

const DURATION_OPTIONS = [
    {id: 15, name: "15 minutes"},
    {id: 30, name: "30 minutes"},
    {id: 45, name: "45 minutes"},
    {id: 60, name: "1 hour"},
    {id: 90, name: "1.5 hours"},
    {id: 120, name: "2 hours"},
    {id: 180, name: "3 hours"},
    {id: 240, name: "4 hours"},
    {id: 360, name: "6 hours"},
    {id: 720, name: "12 hours"}
]

const TIME_INCREMENT_OPTIONS = [
    {id: 15, name: "15 minutes"},
    {id: 30, name: "30 minutes"},
    {id: 60, name: "1 hour"}
]

const MAX_CALENDAR_VIEW_OPTIONS = [
    {id: 7, name: "1 week (7 days)"},
    {id: 30, name: "1 month (30 days)"},
    {id: 60, name: "2 months (60 days)"},
    {id: 90, name: "3 months (90 days)"},
    {id: 180, name: "6 months (180 days)"},
    {id: 365, name: "1 year (365 days)"}
]

const reminderOptions = REMINDERS.map((reminder) => ({value: reminder.id, label: reminder.name}))

const MeetingModal = ({isOpen, onClose, meeting, onSuccess}: Props) => {
    const {t} = useTranslation(["calendar", "common"])
    const model = useModel()
    const queryClient = useQueryClient()
    const userDateFormat = model?.getUserDateFormat?.() || "MM/DD/YYYY"
    const [formData, setFormData] = useState({
        eventTypeId: null as number | null,
        name: "",
        description: "",
        duration: 60,
        capacity: 1,
        startTimeIncrements: 30,
        allowSameDayBooking: false,
        maxCalendarViewDays: null as number | null,
        startAtWall: null as Moment | null,
        endAtWall: null as Moment | null,
        startAtTimezone: model?.getUserTimezone?.() || "America/New_York",
        weeklyHours: [] as WeeklyHour[],
        withPushNotification: false,
        withSms: false,
        withEmail: false,
        notifications: [],
        leadSource: null,
        studentStatus: null,
        groupCalendarId: null as number | null,
        groupCalendarName: null as string | null,
        staffIds: [] as number[],
        defaultStaffId: null as number | null,
        calendarGroupId: null
    })
    const [originalData, setOriginalData] = useState(null)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [hasOverlappingRanges, setHasOverlappingRanges] = useState(false)
    const {isModuleEnable} = useEdularModulesContext()
    const {leadSources} = useAllLeadSources()

    const {data: eventTypes = []} = useQuery({
        queryKey: ["event-types"],
        queryFn: async () => {
            const {data} = await eventTypeService.getAll({
                range: {limit: UNLIMITED_PAGE_SIZE, offset: 0},
                linkedEntities: ["profileLeadSource", "profileStatus"]
            })
            return data
        },
        enabled: isOpen
    })

    const {data: groupCalendars} = useQuery({
        queryKey: ["my-group-calendars"],
        queryFn: async () => {
            const data = await calendarService.getMyCalendars()
            return data.filter((calendar) => calendar.type === "group")
        },
        enabled: isOpen
    })

    const {data: groupCalendarMembers = []} = useQuery({
        queryKey: ["calendar-group-members", formData.calendarGroupId],
        queryFn: async () => {
            const {data} = await calendarGroupsService.getCalendarGroupMembers({
                calendarGroupId: formData.calendarGroupId!,
                range: {page: 1, pageSize: UNLIMITED_PAGE_SIZE},
                filter: {search: ""}
            })
            return data
        },
        enabled: isOpen && !!formData.calendarGroupId
    })

    const selectedStaffs = useMemo(
        () => groupCalendarMembers.filter((member) => formData.staffIds.includes(member.userId)),
        [groupCalendarMembers, formData.staffIds]
    )

    const getStaffOptionLabel = (option: {firstName?: string; lastName?: string; name?: string}) =>
        getFullName(option) || option.name || ""

    const filterStaffOption = (
        option: {label: string; value: string; data: {firstName?: string; lastName?: string; name?: string}},
        inputValue: string
    ) => (inputValue ? getStaffOptionLabel(option.data).toLowerCase().includes(inputValue.toLowerCase()) : true)

    const createMutation = useMutation((payload: CreateMeetingPayload) => calendarService.createMeeting(payload), {
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["meetings"]})
            toastSuccess(t("common:message.createSuccess"))
            onClose()
            onSuccess()
        },
        onError: (err) => {
            handleError(err)
        }
    })

    const editMutation = useMutation((payload: any) => calendarService.editMeeting(payload), {
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["meetings"]})
            toastSuccess(t("common:message.updateSuccess"))
            onClose()
        },
        onError: (err) => {
            handleError(err)
        }
    })

    useEffect(() => {
        if (isOpen) {
            if (meeting) {
                const initialData = {
                    eventTypeId: meeting.jsonData?.edularEventType?.eventTypeId || null,
                    name: meeting.name || "",
                    description: meeting.description || "",
                    duration: meeting.duration || 60,
                    capacity: meeting.capacity || 1,
                    startTimeIncrements: meeting?.startTimeIncrements || 30,
                    allowSameDayBooking: !!meeting.allowSameDayBooking,
                    maxCalendarViewDays: meeting.maxCalendarViewDays ?? null,
                    startAtWall: meeting.startAtUtc ? moment(meeting.startAtUtc) : null,
                    endAtWall: meeting.endAtUtc ? moment(meeting.endAtUtc) : null,
                    startAtTimezone: meeting.startAtTimezone || model?.getUserTimezone?.() || "America/New_York",
                    weeklyHours: meeting.weeklyHours || [],
                    withPushNotification: meeting.jsonData?.remind?.withPushNotification || false,
                    withSms: meeting.jsonData.remind?.withSms || false,
                    withEmail: meeting.jsonData.remind?.withEmail || false,
                    notifications:
                        meeting.jsonData.remind?.notifications?.map((notification) => ({
                            value: notification,
                            label:
                                reminderOptions.find((reminder) => reminder.value === notification)?.label ||
                                `${notification / 60} minutes before`
                        })) || [],
                    leadSource: meeting.jsonData?.newProfileLeadSource,
                    studentStatus: meeting.jsonData?.newProfileStatus,
                    groupCalendarId: meeting.jsonData?.groupCalendarId || null,
                    groupCalendarName: meeting.jsonData?.groupCalendarName || null,
                    staffIds: meeting.jsonData?.staffIds || [],
                    defaultStaffId: meeting.jsonData?.defaultStaffId || null,
                    calendarGroupId: meeting.jsonData?.calendarGroupId || null
                }
                setFormData(initialData)
                setOriginalData(initialData)
            } else {
                setFormData({
                    eventTypeId: null,
                    name: "",
                    description: "",
                    duration: 60,
                    capacity: 1,
                    startTimeIncrements: 30,
                    allowSameDayBooking: false,
                    maxCalendarViewDays: null,
                    startAtWall: moment().startOf("day"),
                    endAtWall: moment().add(90, "day").endOf("day"),
                    startAtTimezone: model?.getUserTimezone?.() || "America/New_York",
                    weeklyHours: [],
                    withPushNotification: false,
                    withSms: false,
                    withEmail: false,
                    notifications: [head(reminderOptions)],
                    leadSource: null,
                    studentStatus: null,
                    groupCalendarId: null,
                    groupCalendarName: null,
                    staffIds: [],
                    defaultStaffId: null,
                    calendarGroupId: null
                })
                setOriginalData(null)
            }
            setErrors({})
            setHasOverlappingRanges(false)
        }
    }, [isOpen, meeting, model])

    useEffect(() => {
        if (!isOpen || !formData.groupCalendarId || formData.calendarGroupId || isEmpty(groupCalendars)) {
            return
        }
        const calendarGroupId =
            find(groupCalendars, (calendar) => calendar.calendar_id === formData.groupCalendarId)?.calendar_group_id ||
            null
        if (!calendarGroupId) {
            return
        }
        setFormData((prev) => ({...prev, calendarGroupId}))
        setOriginalData((prev) => (prev ? {...prev, calendarGroupId} : prev))
    }, [isOpen, formData.calendarGroupId, formData.groupCalendarId, groupCalendars])

    const handleFieldChange = (field: string, value: any) => {
        setFormData((prev) => ({...prev, [field]: value}))
        if (errors[field]) {
            setErrors((prev) => ({...prev, [field]: ""}))
        }
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.name.trim()) {
            newErrors.name = "Event name is required"
        }
        if (!formData.startAtWall) {
            newErrors.startAtWall = "Start date is required"
        }
        if (!formData.endAtWall) {
            newErrors.endAtWall = "End date is required"
        }
        if (formData.startAtWall && formData.endAtWall && formData.startAtWall.isAfter(formData.endAtWall)) {
            newErrors.endAtWall = "End date must be after start date"
        }
        if (formData.weeklyHours.length === 0) {
            newErrors.weeklyHours = "At least one weekly hour range is required"
        }

        if (!isEmpty(newErrors)) {
            toastError("Please enter all the required fields")
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const buildGroupCalendarJsonData = (data: {
        groupCalendarId: number | null
        groupCalendarName: string | null
        calendarGroupId: number | null
        staffIds: number[]
        defaultStaffId: number | null
    }) => {
        const staffIds = data.groupCalendarId ? data.staffIds || [] : []
        const defaultStaffId =
            data.groupCalendarId && data.defaultStaffId && staffIds.includes(data.defaultStaffId)
                ? data.defaultStaffId
                : null

        return {
            groupCalendarId: data.groupCalendarId,
            groupCalendarName: data.groupCalendarName,
            calendarGroupId: data.calendarGroupId,
            staffIds,
            defaultStaffId
        }
    }

    const buildEditPayload = (): EditMeetingPayload => {
        if (!meeting || !originalData) {
            return {meetingId: meeting?.meetingId || 0}
        }
        const newGroupCalendarData = buildGroupCalendarJsonData(formData)
        const oldGroupCalendarData = buildGroupCalendarJsonData(originalData)
        const newData = {
            name: formData.name.trim(),
            description: formData.description,
            duration: formData.duration,
            capacity: formData.capacity,
            startTimeIncrements: formData.startTimeIncrements,
            allowSameDayBooking: formData.allowSameDayBooking,
            maxCalendarViewDays: formData.maxCalendarViewDays,
            startAtWall: formData.startAtWall?.format("YYYY-MM-DD"),
            endAtWall: formData.endAtWall?.format("YYYY-MM-DD"),
            startAtTimezone: formData.startAtTimezone,
            weeklyHours: formData.weeklyHours,
            jsonData: {
                remind: {
                    withPushNotification: formData.withPushNotification,
                    withSms: formData.withSms,
                    withEmail: formData.withEmail,
                    notifications: formData.notifications.map((notification) => notification.value)
                },
                newProfileLeadSource: formData.leadSource,
                newProfileStatus: formData.studentStatus,
                ...newGroupCalendarData
            }
        }
        const oldData = {
            name: originalData.name,
            description: originalData.description,
            duration: originalData.duration,
            capacity: originalData.capacity,
            startTimeIncrements: originalData.startTimeIncrements,
            allowSameDayBooking: originalData.allowSameDayBooking,
            maxCalendarViewDays: originalData.maxCalendarViewDays,
            startAtWall: originalData.startAtWall?.format("YYYY-MM-DD"),
            endAtWall: originalData.endAtWall?.format("YYYY-MM-DD"),
            startAtTimezone: originalData.startAtTimezone,
            weeklyHours: originalData.weeklyHours,
            jsonData: {
                remind: {
                    withPushNotification: originalData.withPushNotification,
                    withSms: originalData.withSms,
                    withEmail: originalData.withEmail,
                    notifications: originalData.notifications.map((notification) => notification.value)
                },
                newProfileLeadSource: originalData.leadSource,
                newProfileStatus: originalData.studentStatus,
                ...oldGroupCalendarData
            }
        }
        const changedData: any = getValuesChangeTwoObj(oldData, newData)
        const payload: EditMeetingPayload = changedData
            ? {meetingId: meeting.meetingId, ...changedData}
            : {meetingId: meeting.meetingId}

        if (formData.eventTypeId !== originalData.eventTypeId || !isEmpty(changedData?.jsonData)) {
            const eventType = eventTypes.find((et) => et.eventTypeId === formData.eventTypeId)
            payload.jsonData = {
                ...meeting.jsonData,
                ...(changedData?.jsonData || {}),
                newProfileLeadSource: formData.leadSource,
                newProfileStatus: formData.studentStatus,
                ...newGroupCalendarData,
                edularEventType: {
                    eventTypeId: eventType?.eventTypeId,
                    color: eventType?.label,
                    name: eventType?.name
                },
                edularDepartment: {
                    name: eventType?.department?.name,
                    departmentId: eventType?.departmentId
                }
            }
        }

        return payload
    }

    const handleSubmit = () => {
        if (!validateForm()) {
            return
        }

        if (meeting) {
            const editPayload = buildEditPayload()
            editMutation.mutate(editPayload)
        } else {
            const eventType = eventTypes.find((et) => et.eventTypeId === formData.eventTypeId)
            const groupCalendarData = buildGroupCalendarJsonData(formData)
            const payload: CreateMeetingPayload = {
                name: formData.name.trim(),
                description: formData.description,
                duration: formData.duration,
                capacity: formData.capacity,
                startTimeIncrements: formData.startTimeIncrements,
                allowSameDayBooking: !!formData.allowSameDayBooking,
                maxCalendarViewDays: formData.maxCalendarViewDays,
                startAtWall: formData.startAtWall!.format("YYYY-MM-DD"),
                endAtWall: formData.endAtWall!.format("YYYY-MM-DD"),
                startAtTimezone: formData.startAtTimezone,
                weeklyHours: formData.weeklyHours,
                jsonData: {
                    edularEventType: {
                        eventTypeId: formData.eventTypeId,
                        color: eventType?.label,
                        name: eventType?.name
                    },
                    edularDepartment: {
                        name: eventType?.department?.name,
                        departmentId: eventType?.departmentId
                    },
                    remind: {
                        withPushNotification: formData.withPushNotification,
                        withSms: formData.withSms,
                        withEmail: formData.withEmail,
                        notifications: formData.notifications.map((notification) => notification.value)
                    },
                    newProfileLeadSource: formData.leadSource,
                    newProfileStatus: formData.studentStatus,
                    ...groupCalendarData
                }
            }
            createMutation.mutate(payload)
        }
    }

    const isLoading = createMutation.isLoading || editMutation.isLoading

    const renderEventTypeOption = (option: Readonly<any>) => {
        return (
            <div className={styles.eventType}>
                <div className={styles.eventType__color} style={{backgroundColor: option.label}} />
                <div className={styles.eventType__label}>
                    {option.name} ({option.code})
                </div>
            </div>
        )
    }

    return (
        <BasePopup isShow={isOpen} onClose={onClose} width="800px">
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>{meeting ? "Edit Meeting" : "Create New Meeting"}</h2>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <FormLabel label="Event Type" />
                        <KlassDropdown
                            options={eventTypes}
                            value={find(eventTypes, (et) => et.eventTypeId === formData.eventTypeId) || null}
                            valueKey="eventTypeId"
                            getOptionLabel={renderEventTypeOption}
                            onChange={(option: any) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    eventTypeId: option?.eventTypeId,
                                    name: option?.name || "",
                                    duration: option?.durationInMinutes || 60,
                                    capacity: option?.capacity || 1,
                                    description: option?.notes || "",
                                    leadSource: option?.profileLeadSourceId
                                        ? {
                                              leadSourceId: option.profileLeadSourceId,
                                              name: option.profileLeadSource?.name
                                          }
                                        : null,
                                    studentStatus: option?.profileStatusId
                                        ? {
                                              statusId: option.profileStatusId,
                                              name: option.profileStatus?.name,
                                              state: option.profileStatus?.state
                                          }
                                        : null
                                }))
                            }}
                            placeholder="Select event type"
                            isClearable
                        />
                    </Col>
                    <Col span={24}>
                        <FormLabel label="Event Name" isRequired />
                        <BaseInput
                            value={formData.name}
                            onChange={(value) => handleFieldChange("name", value)}
                            placeholder="Enter event name"
                            error={!!errors.name}
                        />
                    </Col>
                    <Col span={24}>
                        <FormLabel label="Description" />
                        <BaseEditor
                            value={formData.description}
                            onChange={(value) => handleFieldChange("description", value)}
                            placeholder="Enter description"
                            error={!!errors.description}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Duration" isRequired />
                        <KlassDropdown
                            options={DURATION_OPTIONS}
                            value={find(DURATION_OPTIONS, (opt) => opt.id === formData.duration)}
                            onChange={(option: any) => handleFieldChange("duration", option?.id || 60)}
                            placeholder="Select duration"
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Start Time Increments" isRequired />
                        <KlassDropdown
                            options={TIME_INCREMENT_OPTIONS}
                            value={find(TIME_INCREMENT_OPTIONS, (opt) => opt.id === formData.startTimeIncrements)}
                            onChange={(option: any) => handleFieldChange("startTimeIncrements", option?.id || 30)}
                            placeholder="Select increment"
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Start Date" isRequired />
                        <BaseDatePicker
                            value={formData.startAtWall}
                            onChange={(value) => handleFieldChange("startAtWall", value)}
                            format={userDateFormat}
                            error={!!errors.startAtWall}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="End Date" isRequired />
                        <BaseDatePicker
                            value={formData.endAtWall}
                            onChange={(value) => handleFieldChange("endAtWall", value)}
                            format={userDateFormat}
                            error={!!errors.endAtWall}
                            disabledDate={(current) =>
                                formData.startAtWall ? current && current < formData.startAtWall : false
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Timezone" />
                        <TimezoneDropdown
                            value={formData.startAtTimezone}
                            onChange={(timezone) => {
                                const timezoneValue = typeof timezone === "string" ? timezone : timezone?.value || ""
                                handleFieldChange("startAtTimezone", timezoneValue)
                            }}
                            placeholder="Select timezone"
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("calendar.eventTypes.capacity")} />
                        <BaseInput
                            type="number"
                            min={1}
                            value={formData.capacity ?? ""}
                            onChange={(value) => handleFieldChange("capacity", value ? Math.max(1, Number(value)) : 1)}
                            placeholder={t("calendar.eventTypes.capacity")}
                            onKeyDown={preventMinusCharacter}
                        />
                    </Col>
                    <Col span={12}>
                        <Checkbox
                            checked={!!formData.allowSameDayBooking}
                            onChange={(e) => handleFieldChange("allowSameDayBooking", e.target.checked)}>
                            Allow same-day booking
                        </Checkbox>
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Max Calendar View" />
                        <KlassDropdown
                            options={MAX_CALENDAR_VIEW_OPTIONS}
                            value={find(MAX_CALENDAR_VIEW_OPTIONS, (opt) => opt.id === formData.maxCalendarViewDays)}
                            onChange={(option: {id: number; name: string} | null) =>
                                handleFieldChange("maxCalendarViewDays", option?.id ?? null)
                            }
                            placeholder="No limit"
                            isClearable
                        />
                    </Col>
                    <Col span={24}>
                        <FormLabel label="Weekly Hours" isRequired />
                        <WeeklyHoursEditor
                            weeklyHours={formData.weeklyHours}
                            onChange={(weeklyHours) => handleFieldChange("weeklyHours", weeklyHours)}
                            error={!!errors.weeklyHours}
                            onOverlapChange={setHasOverlappingRanges}
                        />
                    </Col>
                    <Col span={24}>
                        <div className={styles.reminderMethods}>
                            <div>
                                <FormLabel label="Reminders" />
                                <KlassDropdown
                                    creatable
                                    isMulti
                                    isClearable
                                    placeholder="Select or type reminder"
                                    formatCreateLabel={(inputValue) => `Use "${inputValue} minutes"`}
                                    options={reminderOptions}
                                    valueKey="value"
                                    labelKey="label"
                                    onChange={(options) => {
                                        const newOptions =
                                            map(options, (option) =>
                                                option.__isNew__
                                                    ? isNaN(option.value)
                                                        ? null
                                                        : {
                                                              value: +option.value * 60,
                                                              label: `${option.value} minutes before`
                                                          }
                                                    : option
                                            ).filter(Boolean) ?? []
                                        handleFieldChange("notifications", newOptions)
                                    }}
                                    value={formData.notifications}
                                    error={!!errors["reminders"]}
                                />
                            </div>
                            <div>
                                <Checkbox
                                    checked={!!formData.withPushNotification}
                                    disabled={isEmpty(formData.notifications)}
                                    onChange={(e) => handleFieldChange("withPushNotification", e.target.checked)}>
                                    {t("calendar.sendMobilePushNotificationReminder")}
                                </Checkbox>
                            </div>
                            {isModuleEnable(Modules.Sms) && (
                                <div>
                                    <Checkbox
                                        checked={!!formData.withSms}
                                        disabled={isEmpty(formData.notifications)}
                                        onChange={(e) => handleFieldChange("withSms", e.target.checked)}>
                                        {t("calendar.sendSmsReminder")}
                                    </Checkbox>
                                </div>
                            )}
                            <div>
                                <Checkbox
                                    checked={!!formData.withEmail}
                                    disabled={isEmpty(formData.notifications)}
                                    onChange={(e) => handleFieldChange("withEmail", e.target.checked)}>
                                    {t("calendar.sendEmailReminder")}
                                </Checkbox>
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Lead Source" />
                        <KlassDropdown
                            options={leadSources}
                            value={find(leadSources, (ls) => ls.id === formData.leadSource?.leadSourceId) || null}
                            valueKey="id"
                            labelKey="name"
                            onChange={(option: {id: number; name: string} | null) =>
                                handleFieldChange(
                                    "leadSource",
                                    option ? {leadSourceId: option.id, name: option.name} : null
                                )
                            }
                            placeholder="Select lead source"
                            isClearable
                            error={!!errors.leadSource}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Student Status" />
                        <NewStudentStatusSelectFilter
                            value={(formData.studentStatus as StudentStatus) || null}
                            onChange={(newValue) =>
                                handleFieldChange(
                                    "studentStatus",
                                    newValue
                                        ? {statusId: newValue.statusId, name: newValue.name, state: newValue.state}
                                        : null
                                )
                            }
                            isClearable
                            error={!!errors.studentStatus}
                        />
                    </Col>
                    <Col span={24}>
                        <FormLabel label="Group Calendar" />
                        <KlassDropdown
                            options={groupCalendars}
                            value={
                                find(groupCalendars, (calendar) => calendar.calendar_id === formData.groupCalendarId) ||
                                null
                            }
                            valueKey="calendar_id"
                            labelKey="name"
                            onChange={(option: MyCalendar) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    groupCalendarId: option?.calendar_id || null,
                                    groupCalendarName: option?.name || null,
                                    calendarGroupId: option?.calendar_group_id || null,
                                    staffIds: [],
                                    defaultStaffId: null
                                }))
                            }
                            placeholder="Select group calendar"
                            isClearable
                        />
                    </Col>
                    {!!formData.groupCalendarId && (
                        <>
                            <Col span={12}>
                                <FormLabel label="Staff" />
                                <KlassDropdown
                                    options={groupCalendarMembers}
                                    value={selectedStaffs}
                                    valueKey="userId"
                                    getOptionLabel={getStaffOptionLabel}
                                    filterOption={filterStaffOption}
                                    onChange={(options: {userId: number}[] | null) => {
                                        const staffIds = (options || []).map((option) => option.userId)
                                        setFormData((prev) => ({
                                            ...prev,
                                            staffIds,
                                            defaultStaffId:
                                                prev.defaultStaffId && staffIds.includes(prev.defaultStaffId)
                                                    ? prev.defaultStaffId
                                                    : null
                                        }))
                                    }}
                                    placeholder="Select staff"
                                    isMulti
                                    isClearable
                                />
                            </Col>
                            <Col span={12}>
                                <FormLabel label="Default Staff" />
                                <KlassDropdown
                                    options={selectedStaffs}
                                    value={
                                        find(selectedStaffs, (staff) => staff.userId === formData.defaultStaffId) ||
                                        null
                                    }
                                    valueKey="userId"
                                    getOptionLabel={getStaffOptionLabel}
                                    filterOption={filterStaffOption}
                                    onChange={(option: {userId: number} | null) =>
                                        handleFieldChange("defaultStaffId", option?.userId || null)
                                    }
                                    placeholder="Select default staff"
                                    isClearable
                                    isDisabled={isEmpty(selectedStaffs)}
                                />
                            </Col>
                        </>
                    )}
                </Row>
                <div className={styles.modalFooter}>
                    <BaseButton
                        title={t("common:action.cancel")}
                        onClick={onClose}
                        disabled={isLoading}
                        variant="secondary"
                    />
                    <BaseButton
                        title={meeting ? t("common:action.save") : t("common:action.create")}
                        onClick={handleSubmit}
                        loading={isLoading}
                        type="primary"
                        disabled={
                            hasOverlappingRanges || isEmpty(formData.name.trim()) || isEmpty(formData.weeklyHours)
                        }
                    />
                </div>
            </div>
        </BasePopup>
    )
}

export default MeetingModal
